import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Data, NavigationEnd, Router } from '@angular/router';
import { filter, map, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { getDeepestRoute } from '@core/helpers';
import { HcService } from '@project/services';

@Component({
  selector: 'app-authorization-layout',
  templateUrl: './authorization-layout.component.html',
  styleUrls: ['./authorization-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthorizationLayoutComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  public shouldHideHeader = false;

  constructor(
    public hcService: HcService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // 1. When page loads, get initial route data to toggle header visibility
    const initialData = getDeepestRoute(this.activatedRoute.snapshot).data;
    this.updateHeaderVisibility(initialData);

    // 2. Subscribe to route changes to update header visibility
    this.onRouteChange((data) => this.updateHeaderVisibility(data));

    // Remote HC config can change in realtime (background/logo). This component is OnPush and
    // its template already reads hcService.getHcConfig() fresh, but without this it stays dirty
    // only after a manual reload since the Firestore listener alone doesn't mark it for check.
    this.hcService.configUpdated$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateHeaderVisibility(data: Data): void {
    this.shouldHideHeader = data['hideHeader'] === true;
  }

  private onRouteChange(cb: (routeData: Data) => void): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => getDeepestRoute(this.activatedRoute.snapshot)),
        map((route) => route.data),
        takeUntil(this.destroy$),
      )
      .subscribe((data) => cb(data));
  }
}
